import { type CallhomeCamera, type SiteId } from '@solinkcorp/api-types';
import { useSite } from '@solinkcorp/callhome-data';
import { useMemo } from 'react';
import usePeopleTrackingRawData from './usePeopleTrackingRawData';

/**
 * Structural view of one camera's vision analytics config. The full VMS types live in the web
 * app, which this package can't import, so we describe only the fields we read.
 */
interface VisionAnalyticsCameraConfig {
  enabled?: boolean;
  cam_id?: string;
  event_lines?: { metadata?: { aiTagIds?: string[] } }[] | null;
  dwell_event_polygons?: { metadata?: { aiTagIds?: string[] } }[] | null;
}

/**
 * Flatten the two `rawData` shapes returned by the line crossing config endpoint into a common
 * list. Multicam responses are keyed by VMS camera id; older single-camera devices return the
 * camera config itself, with its id under `cam_id`.
 */
function toCameraConfigs(
  rawData: Record<string, unknown>,
): { vmsCameraId: string; config: VisionAnalyticsCameraConfig }[] {
  if (typeof rawData.cam_id === 'string') {
    const config = rawData as VisionAnalyticsCameraConfig;
    return [{ vmsCameraId: rawData.cam_id, config }];
  }

  return Object.entries(rawData).flatMap(([key, value]) => {
    if (!value || typeof value !== 'object') return [];
    const config = value as VisionAnalyticsCameraConfig;
    return [{ vmsCameraId: config.cam_id ?? key, config }];
  });
}

/** Every AI tag associated with any line or zone configured on this camera. */
function aiTagIdsOnCamera(config: VisionAnalyticsCameraConfig): Set<string> {
  const ids = new Set<string>();
  for (const line of config.event_lines ?? []) {
    for (const id of line?.metadata?.aiTagIds ?? []) ids.add(id);
  }
  for (const polygon of config.dwell_event_polygons ?? []) {
    for (const id of polygon?.metadata?.aiTagIds ?? []) ids.add(id);
  }
  return ids;
}

/**
 * Resolve the cameras whose vision analytics config references at least one of `tagIds`.
 *
 * @param rawData Line crossing config for the site's device, in either the single- or
 *   multi-camera shape.
 * @param siteCameras CallHome cameras for the site, used to turn VMS camera ids into cameras.
 * @param tagIds AI tag ids a widget is configured to display.
 * @returns Matching cameras, sorted by VMS camera id so rotation order is stable across renders.
 */
export function resolveCamerasForAiTags(
  rawData: Record<string, unknown> | undefined,
  siteCameras: readonly CallhomeCamera[] | undefined,
  tagIds: readonly string[],
): CallhomeCamera[] {
  if (!rawData || !siteCameras?.length || !tagIds.length) return [];

  const wanted = new Set(tagIds);
  const matched = new Map<string, CallhomeCamera>();

  for (const { vmsCameraId, config } of toCameraConfigs(rawData)) {
    if (config.enabled === false) continue;

    const tagsOnCamera = aiTagIdsOnCamera(config);
    if (![...wanted].some((id) => tagsOnCamera.has(id))) continue;

    const camera = siteCameras.find((c) => c.cameraId === vmsCameraId);
    if (camera) matched.set(camera.cameraId, camera);
  }

  return [...matched.values()].sort((a, b) => a.cameraId.localeCompare(b.cameraId));
}

/**
 * Cameras that should be shown for a set of AI tags on a site.
 *
 * @param siteId Site whose vision analytics config and cameras to read.
 * @param tagIds AI tag ids a widget is configured to display.
 * @returns `cameras`: matching cameras in a stable order, empty when nothing matched or the data
 *   has not arrived. `isLoaded`: whether the site and its vision analytics config have arrived,
 *   so callers can tell "no match" apart from "not fetched yet".
 */
export default function useCamerasForAiTags(
  siteId: SiteId | string | undefined,
  tagIds: readonly string[],
): { cameras: CallhomeCamera[]; isLoaded: boolean } {
  const { rawData } = usePeopleTrackingRawData(siteId);
  const { site } = useSite({ siteId: siteId as SiteId });
  const siteCameras = site?.cameras;

  const cameras = useMemo(
    () => resolveCamerasForAiTags(rawData, siteCameras, tagIds),
    [rawData, siteCameras, tagIds],
  );

  return { cameras, isLoaded: !!rawData && !!site };
}
